import { useState } from 'react'
import type { ReactNode } from 'react'
import { cannectDefaultProductNoTarg } from '@cannect/assets/img'
import { ProgressiveDiscountBadge } from '@cannect/new-components/ProgressiveDiscountBadge'
import { TRecipeOrderItem } from '@cannect/services/resources/recipeAccess'
import { pickProductCoverImagePath, ProductMediaItem } from 'utils/productMedia'
import { useRecipeBrandColors } from '../useRecipeBrandColors'
import * as Style from './styles'

type RecipeProductProps = {
  product: TRecipeOrderItem
  /** Pill "Sabor: X" (`FlavorSelect` reusado) — injetado pelo pai, não redesenhado aqui. */
  flavorSlot?: ReactNode
}

const formatBrl = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)

function toMediaList(media: TRecipeOrderItem['product']['media']): ProductMediaItem[] {
  if (!media) return []
  return Array.isArray(media) ? media : [media]
}

// `product.brand_name` (contrato provisório, Andarilho 19/10 — Lince ainda
// revisando o delta do backend): string só com o nome da marca, ou `null`
// quando não houver — a linha some, sem "null" na tela.
function resolveProductImage(product: TRecipeOrderItem['product']): string {
  return pickProductCoverImagePath(toMediaList(product.media)) || cannectDefaultProductNoTarg
}

// Achado 51 (humano, captura real contra a API de verdade): o preço era uma
// COLUNA própria ao lado do nome (`Style.PriceContainer`), o que espremia o
// nome em ~9 linhas com o container em 1120px. No protótipo
// (`Desktop-B-produtos.dc.html`) o preço fica na MESMA linha de
// baixo que Sabor/Quantidade, alinhado à direita — não é uma coluna.
export default function RecipeProduct({ product: item, flavorSlot }: RecipeProductProps) {
  const colors = useRecipeBrandColors()
  const { product } = item
  const unitPrice = item.unit_price
  const lineDiscount = item.discount || 0
  const lineTotal = item.amount
  const lineSubtotal = unitPrice * item.quantity
  const hasDiscount = lineDiscount > 0.005
  const discountPercent = hasDiscount && lineSubtotal > 0 ? Math.round((lineDiscount / lineSubtotal) * 100) : 0

  const [imgSrc, setImgSrc] = useState(() => resolveProductImage(product))

  return (
    <Style.RecipeProductContainer>
      <img
        src={imgSrc}
        alt={product.translated_name}
        onError={() => setImgSrc(cannectDefaultProductNoTarg)}
        className="h-21 w-21 shrink-0 rounded-xl object-contain lg:h-[132px] lg:w-[132px]"
      />
      <Style.ProductMainInfo>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`w-fit rounded-[110px] px-[9px] py-[3px] text-[11px] font-semibold ${colors.softBg} ${colors.softText}`}>
            ℞ Prescrito pelo seu médico
          </span>
          <ProgressiveDiscountBadge productId={product.id} />
        </div>
        {product.brand_name && (
          <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{product.brand_name}</p>
        )}
        <p className="mt-0.5 w-full text-sm font-semibold leading-snug text-gray-950 lg:text-[17px]">
          {product.translated_name}
        </p>
        <span className="text-xs text-gray-500">Receita indica {item.quantity} frasco(s)</span>

        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {flavorSlot}
            <span className="text-sm text-gray-500">Quantidade {item.quantity}</span>
          </div>
          <div className="flex flex-col items-end">
            {hasDiscount && (
              <span className="text-xs text-gray-400">
                <Style.StrikethroughPrice>{formatBrl(lineSubtotal)}</Style.StrikethroughPrice>
                {discountPercent > 0 && <Style.DiscountBadge>-{discountPercent}%</Style.DiscountBadge>}
              </span>
            )}
            <span className="text-lg font-bold text-gray-950 lg:text-[22px]">{formatBrl(lineTotal)}</span>
          </div>
        </div>
      </Style.ProductMainInfo>
    </Style.RecipeProductContainer>
  )
}
